<template>
  <div class="paginationQuery">
    <div class="paginationQuery-header flex-b">
      <div class="header-left">
        <slot name="headerLeft"></slot>
      </div>
      <div class="header-right mb-10" v-if="searchList.length">
        <a-space v-if="isSelect" class="mr-8">
          <template v-for="item in btnList" :key="item.key">
            <a-button @click="handleClick(item.key)">
              <template #icon>
                <component :is="icons[item.icon]" v-if="item.icon?.length" />
              </template>
              <span>{{ item.label }}</span>
            </a-button>
          </template>
        </a-space>
        <a-space>
          <template v-for="(item, index) in searchList">
            <a-input-search
              v-if="item.type === 'search'"
              :key="index"
              :disabled="isSelect"
              v-model:value="states.form[item.key]"
              :placeholder="item.placeholder"
              style="width: 200px"
              @search="searchHandle"
            />
            <a-button
              @click="buttonClick(item)"
              :disabled="isSelect"
              v-if="item.type === 'sort'"
              :key="index"
            >
              <template #icon>
                <span>
                  <SwapRightOutlined :rotate="-90" style="font-size: 16px" />
                  <SwapRightOutlined :rotate="90" style="margin-left: -8px; font-size: 16px" />
                </span>
              </template>
              <span>{{ item.placeholder }}</span>
            </a-button>
            <a-select
              v-if="item.type === 'select'"
              :key="index"
              :disabled="isSelect"
              v-model:value="states.form[item.key]"
              show-search
              :placeholder="item.placeholder"
              style="width: 200px"
              :options="item.options"
              :filter-option="filterOption"
              @change="searchHandle"
            ></a-select>
            <a-range-picker
              v-if="item.type === 'time'"
              :key="index"
              :disabled="isSelect"
              v-model:value="states.form[item.key]"
              show-time
              format="YYYY-MM-DD HH:mm:ss"
              style="width: 360px"
              @change="searchHandle"
            />
          </template>
        </a-space>
      </div>
    </div>
    <div
      class="paginationQuery-body"
      ref="scrollContainer"
      :style="{
        maxHeight: props.height || '200px'
      }"
    >
      <div class="status-box empty" v-if="!states.total">
        <slot name="empty">
          <a-empty :image="simpleImage" />
        </slot>
      </div>
      <template v-else>
        <slot name="default"></slot>
      </template>
      <div class="flex mb-10" v-show="states.loading">
        <a-spin />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import httpRequest from '@/service/request/indexBase'
import dayjs from 'dayjs'
import {
  Space as ASpace,
  Input as AInput,
  Button as AButton,
  RangePicker as ARangePicker,
  Empty as AEmpty,
  Spin as ASpin,
  Select as ASelect
} from 'ant-design-vue'
import * as icons from '@ant-design/icons-vue'
import { SwapRightOutlined } from '@ant-design/icons-vue'
import { debounce, throttle } from 'lodash'

const AInputSearch = AInput.Search
const simpleImage = AEmpty.PRESENTED_IMAGE_SIMPLE

const emits = defineEmits(['updateList', 'fnClick'])
const props = defineProps({
  height: {
    type: String || Number
  },
  // 请求url
  url: {
    type: String,
    required: true
  },
  // 请求方式
  method: {
    type: String,
    default: 'get'
  },
  // 分页数量
  pageSize: {
    type: Number,
    default: 10
  },
  // 搜索字段集
  searchList: {
    type: Array,
    default: () => []
  },
  // 操作按钮字段集
  btnList: {
    type: Array,
    default: () => []
  },
  // 请求额外添加外部引入的参数
  searchData: {
    type: Object,
    default: () => {}
  },
  // 是否选择了数据
  isSelect: {
    type: Boolean,
    default: false
  },
  // 是否初始化请求
  initRequest: {
    type: Boolean,
    default: true
  },
  // 是否显示loading
  showLoading: {
    type: Boolean,
    default: true
  },
  // 请求额外添加外部引入的参数
  searchParams: {
    type: Object,
    default: () => {}
  }
})

const scrollContainer = ref(null)
const dataList = ref([])
const searchVal = ref({})
const states = reactive({
  total: 0,
  loading: false,
  form: {
    current: 1,
    size: props.pageSize
  }
})
const onSearch = (paramsVal = {}, type) => {
  return new Promise((resolve) => {
    if (!props.url.length) return resolve(false)
    if (type == 'init' && scrollContainer.value) {
      states.form.current = 1
      searchVal.value = paramsVal
    }
    const dataForm = JSON.parse(JSON.stringify(states.form))
    // 查找类型为 'time' 的项
    const timeItems = props.searchList.filter((item) => item.type === 'time')
    // 如果找到了，并且对象中有与 'key' 相同的属性，则替换
    timeItems.length &&
      timeItems.forEach((timeItem) => {
        // 检查对象中是否有与 'key' 相同的属性
        if (Object.prototype.hasOwnProperty.call(dataForm, timeItem.key)) {
          // 将对象中的 'key' 替换为 'queryKey' 中的值
          // 注意：这里假设 queryKey 只有一个值，如果 queryKey 有多个值，你可能需要根据具体需求来处理
          dataForm[timeItem.queryKey[0]] =
            dataForm[timeItem.key] && dataForm[timeItem.key][0]
              ? dayjs(dataForm[timeItem.key][0]).format('YYYY-MM-DD HH:mm:ss')
              : ''
          dataForm[timeItem.queryKey[1]] =
            dataForm[timeItem.key] && dataForm[timeItem.key][1]
              ? dayjs(dataForm[timeItem.key][1]).format('YYYY-MM-DD HH:mm:ss')
              : ''
          // 删除原始键
          delete dataForm[timeItem.key]
        }
      })
    states.loading = true
    let search = {
      url: props.url
    }
    if (props.method === 'post') {
      search.data = {
        ...dataForm,
        ...props.searchData,
        ...searchVal.value
      }
      search.params = {
        ...props.searchParams
      }
    } else {
      search.params = {
        ...dataForm,
        ...props.searchData,
        ...props.searchParams,
        ...searchVal.value
      }
    }
    httpRequest[props.method](search).then(({ data }) => {
      if (data) {
        states.total = parseInt(data.total || data.length)
        if (type == 'init') {
          dataList.value = data.records || data.data || data || []
        } else {
          dataList.value.push(...(data.records || data.data || data || []))
        }

        emits('updateList', dataList.value, search, states.total)
      } else {
        states.total = 0
      }
      resolve(dataList.value)
      states.loading = false
    })
  })
}

const debouncedOnSearch = debounce(
  onSearch,
  300, // 防抖间隔，按需调整
  { leading: false, trailing: true }
)
const buttonClick = (i) => {
  states.form[i.key] = states.form[i.key] === i.list[0] ? i.list[1] : i.list[0]
  onSearch({}, 'init')
}
const handleClick = (key) => {
  emits('fnClick', key)
}
const searchHandle = () => {
  onSearch({}, 'init')
}
const filterOption = (input, option) => {
  return option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
}
const initData = (val) => {
  val.forEach((i) => {
    states.form[i.key] = i.default
  })
  states.form.current = 1
  states.form.size = props.pageSize
}
const handleScroll = (event) => {
  const { scrollTop, clientHeight, scrollHeight } = event.target
  if (
    scrollTop + clientHeight >= scrollHeight - 20 &&
    !states.loading &&
    dataList.value.length < states.total
  ) {
    // 预留20px的提前量
    console.log('滚动到底部')
    states.form.current++
    onSearch()
  }
}
const throttledHandleScroll = throttle(handleScroll, 500)
onMounted(() => {
  scrollContainer.value.addEventListener('scroll', throttledHandleScroll)
})
watch(
  () => props.searchList,
  (val) => {
    if (val.length) {
      initData(val)
    }
  }
)
watch(
  () => props.url,
  (val) => {
    if (val.length && props.initRequest) {
      onSearch({}, 'init')
    }
  },
  { immediate: true }
)
defineExpose({ onSearch, initData, debouncedOnSearch })
</script>

<style lang="less" scoped>
.paginationQuery {
  width: 100%;
}

.paginationQuery-header {
  width: 100%;

  .header-left {
    flex: 1;
  }

  .header-right {
    width: auto;
  }
}

.paginationQuery-body {
  width: 100%;
  overflow-y: auto;
  position: relative;
  scrollbar-width: none;
}

.status-box {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff8b;
  z-index: 10;
}

:deep(.ant-pagination-options-size-changer) {
  width: 110px;
}
</style>
